import { useContext, useMemo } from 'react';
import { FormikContext, FormikContextType } from 'formik';
import { StoreProductValues } from '../CreateProductForm';
import { useQuery } from '@tanstack/react-query';
import { Category } from '../../../../models/Category/Category';
import { StorageRoom } from '../../../../models/Storage/StorageRoom';
import { StorageStand } from '../../../../models/Storage/StorageStand';
import { Attribute } from '../../../../models/Attribute/Attribute';
import { Feature } from '../../../../models/Feature/Feature';
import style from './steps.module.scss';

export default function ProductSummary() {
  const { values } = useContext(
    FormikContext
  ) as FormikContextType<StoreProductValues>;
  const { data: categories = [] } = useQuery(['categories'], () =>
    Category.all()
  );
  const categoryMap = useMemo(() => Category.toMap(categories), [categories]);

  const { data: stands = [] } = useQuery(
    ['storage-room-stands'],
    StorageStand.all
  );
  const standMap = useMemo(
    () => new Map(stands.map((s) => [s.id, s])),
    [stands]
  );

  const { data: attributes = [] } = useQuery(['attributes'], () =>
    Attribute.all('values')
  );
  const attributeValueMap = useMemo(
    () => Attribute.toValueMap(attributes),
    [attributes]
  );

  const { data: features = [] } = useQuery(['features'], () =>
    Feature.all('values')
  );
  const featureMap = useMemo(() => Feature.toMap(features), [features]);
  const attributeMap = useMemo(() => Attribute.toMap(attributes), [attributes]);
  // Helper to render attribute/feature/grouped lists
  const renderList = (arr?: any[]) =>
    arr && arr.length > 0 ? (
      arr.join(', ')
    ) : (
      <span style={{ opacity: 0.5 }}>—</span>
    );

  // Get category name:
  const categoryLabel =
    typeof values.category === 'number' ? (
      categoryMap.get(values.category)?.name
    ) : (
      <span style={{ opacity: 0.5 }}>Nije izabrano</span>
    );

  // Get stand (polica) name:
  const standLabel =
    typeof values.storage_room_stand_id === 'number' ? (
      standMap.get(values.storage_room_stand_id)?.name
    ) : (
      <span style={{ opacity: 0.5 }}>Nije izabrano</span>
    );

  // --- NEW IMAGE PREVIEW SECTION ---
  const gallery = values.gallery_media || [];
  const coverId = values.cover_image_id;

  return (
    <div className={style.summaryWrapper}>
      <div className={style.summaryTitle}>Pregled proizvoda</div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Ime:</span>
        <span className={style.summaryValue}>
          {values.name || <i>Nije uneto</i>}
        </span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>SKU:</span>
        <span className={style.summaryValue}>
          {values.sku || <i>Nije uneto</i>}
        </span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Kategorija:</span>
        <span className={style.summaryValue}>{categoryLabel}</span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Lokacija (polica):</span>
        <span className={style.summaryValue}>{standLabel}</span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Kratak opis:</span>
        <span className={style.summaryValue}>
          {values.short_description || <i>—</i>}
        </span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Cena:</span>
        <span className={style.summaryValue}>{values.price} RSD</span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Penal cena:</span>
        <span className={style.summaryValue}>{values.penalty_price} RSD</span>
      </div>
      <div className={style.summaryRow}>
        <span className={style.summaryLabel}>Poeni:</span>
        <span className={style.summaryValue}>{values.points}</span>
      </div>

      <div className={`${style.summaryRow} ${style.summaryList}`}>
        <span className={style.summaryLabel}>Atributi:</span>
        <div className={style.summaryValueBlock}>
          {values.attributes.length === 0 ? (
            <i>—</i>
          ) : (
            values.attributes.map((attributeID) => {
              const attribute = attributeMap.get(attributeID);
              if (!attribute) return null;
              const chosenValueIDs = values.attribute_values[attributeID] || [];
              const valueNames = (attribute.options || [])
                .filter((opt) => chosenValueIDs.includes(opt.id))
                .map((opt) => opt.value)
                .join(', ');
              return (
                <div key={attributeID}>
                  <strong>{attribute.name}:</strong> {valueNames || <i>—</i>}
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className={`${style.summaryRow} ${style.summaryList}`}>
        <span className={style.summaryLabel}>Karakteristike:</span>
        <div className={style.summaryValueBlock}>
          {values.features.length === 0 ? (
            <i>—</i>
          ) : (
            values.features.map((featureID) => {
              const feature = featureMap.get(featureID);
              if (!feature) return null;
              const chosenValueIDs = values.feature_values[featureID] || [];
              const valueNames = (feature.options || [])
                .filter((opt) => chosenValueIDs.includes(opt.id))
                .map((opt) => opt.value)
                .join(', ');
              return (
                <div key={featureID}>
                  <strong>{feature.name}:</strong> {valueNames || <i>—</i>}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className={style.summaryRow} style={{ borderBottom: 'none' }}>
        <span className={style.summaryLabel}>Galerija:</span>
        <span className={style.summaryGallery}>
          {gallery.length === 0 && <span style={{ opacity: 0.5 }}>—</span>}
          {gallery.map((img: any, idx) => {
            // Use fullUrl if available (Media class), otherwise build from env + url, fallback to path/preview
            let imgSrc = '';
            if (img.fullUrl) {
              imgSrc = img.fullUrl;
            } else if (img.url) {
              // If the url does NOT start with 'http', prefix it
              imgSrc = img.url.startsWith('http')
                ? img.url
                : `${process.env.REACT_APP_ASSET_URL || ''}${img.url}`;
            } else if (img.path) {
              imgSrc = img.path;
            } else if (img.preview) {
              imgSrc = img.preview;
            }
            return (
              <span
                key={img.id || idx}
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  marginRight: 8
                }}
              >
                <img
                  className={style.summaryImage}
                  src={imgSrc}
                  alt=""
                  style={{
                    border:
                      img.id === coverId ? '2px solid gold' : '1px solid #eee',
                    width: 70,
                    height: 70,
                    objectFit: 'cover'
                  }}
                />
                {img.id === coverId && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      background: 'gold',
                      color: '#222',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontWeight: 800,
                      fontSize: 13
                    }}
                  >
                    COVER
                  </span>
                )}
              </span>
            );
          })}
        </span>
      </div>
    </div>
  );
}
